import { useContact, useLease } from "@/OneLiners";
import {
    ActionResponse,
    ActionStatusHelper,
    DOMAIN_CONFIGS,
    DomainFormHook,
    DomainHookProps,
    useAppEnum,
    useFormLifeCycle,
} from "@/Shared";

import { LeaseFormViewModel } from "../ViewModels/LeaseFormViewModel";
import { useLeaseEnricher } from "./useLeaseEnricher";
import { useContactEnricher } from "@/Domains/Contact";
import { LeaseValidationSchema } from "../LeaseFormValidation";
import { LeaseModel } from "../LeaseModel";

/**
 * useLeaseForm hook – manages contact form data and interactions.
 * 
 * Author: Samano CASTRE  
 * Date: July 17, 2025
 * 
 * Responsibilities:
 * - Initialises contact form data by fetching current contact and agent list
 * - Tracks form submission state and response (create or update)
 * - Exposes form logic to the component layer
 * 
 * Behavior:
 * - On mount, fetches the current contact (if editing) and the list of agents
 * - Automatically switches between `createLease` or `updateLease` based on contact presence
 * - Tracks the response of the latest action to provide submission feedback
 */
export const useLeaseForm = (props: DomainHookProps): DomainFormHook => {
    const { permissionHook } = props;
    const { domainType } = DOMAIN_CONFIGS.Lease;

    /**
     * Contact domain operations and related backend state.
     * Exposes CRUD actions, current entity fetching and associated responses.
     */
    const leaseHook = useLease();

    /**
    * Users data (used for agent resolution and audit enrichment)
    */
    const {
        dataMap: contactMap,
        stateResponse: contactResponse
    } = useContact();
    const { enricherResponse: contactEnricherResponse, getEnrichedContactMap } = useContactEnricher();

    /**
     * Contact enrichment layer.
     * Provides additional data (users, computed fields, etc.)
     * required to build the form view model.
     */
    const {
        transactionMap,
        enricherResponse,
        getEnrichedLease,
    } = useLeaseEnricher();

    /**
         * Contact-related enums exposed to the UI layer.
         * Used to build the form view model.
         */
    const { LeaseEnums } = useAppEnum();

    const response: ActionResponse = {
        status: ActionStatusHelper.combine(
            leaseHook.stateResponse.status,
            enricherResponse.status,
            contactEnricherResponse.status,
            contactResponse.status,
            permissionHook.stateResponse.status
        ),
        error: leaseHook.stateResponse.error ??
            contactResponse.error ??
            enricherResponse.error ??
            contactEnricherResponse.error
    }

    const lifeCycleHook = useFormLifeCycle({
        response,
        domainType,
        props,
        entityHook: leaseHook,
        buildVM: (currentEntity: LeaseModel) => {
            return new LeaseFormViewModel(
                LeaseEnums,
                getEnrichedContactMap(contactMap),
                transactionMap,
                getEnrichedLease(currentEntity)
            )
        }
    });


    /**
     * Public API exposed to the component layer.
     * Only data and actions strictly required by the UI are returned.
     */
    return {
        // Form
        ...lifeCycleHook,
        ValidationSchema: LeaseValidationSchema(LeaseEnums),
    }
}